import React, { createContext, useEffect, useState, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Payment_Information } from '../screens/checkout/components/Receipt';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../redux/helper';
import {
  addSingleSetting,
  selectProductCardLayout,
} from '../redux/SettingsSlice';

interface Receipt {
  order: any;
  orderID: string;
  Payment_Information: Payment_Information;
  ReceiptID: string;
}

interface ExtrasContextType {
  refreshing: boolean;
  setRefreshing: React.Dispatch<React.SetStateAction<boolean>>;
  networkAvailableGlobal: boolean;
  setNetworkAvailableGlobal: React.Dispatch<React.SetStateAction<boolean>>;
  continueOffline: boolean;
  setContinueOffline: React.Dispatch<React.SetStateAction<boolean>>;
  isRowLayout: boolean;
  setIsRowlayout: React.Dispatch<React.SetStateAction<boolean>>;
  receipt: Receipt;
  setReceipt: React.Dispatch<React.SetStateAction<Receipt>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ExtrasContext = createContext<ExtrasContextType>(
  {} as ExtrasContextType,
);

interface ExtrasContextProviderObject {
  children: ReactNode;
}

export const ExtrasContextProvider: React.FC<ExtrasContextProviderObject> = ({
  children,
}) => {
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [networkAvailableGlobal, setNetworkAvailableGlobal] = useState(true);
  const [continueOffline, setContinueOffline] = useState<boolean>(false);
  const [isRowLayout, setIsRowLayoutState] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const reduxLayout = useAppSelector(selectProductCardLayout);
  const [receipt, setReceipt] = useState<Receipt>({
    order: [],
    orderID: '',
    Payment_Information: {
      bp_payment_amount: 0,
      bp_payment_already_paid_amount: 0,
      bp_payment_amount_due: 0,
      bp_payment_amount_refunded: 0,
    },
    ReceiptID: '',
  });

  const IS_ROW_LAYOUT_KEY = 'extras:isRowLayout';

  // Backwards-compat: read previous persisted value once (before Redux took over)
  useEffect(() => {
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(IS_ROW_LAYOUT_KEY);
        if (stored !== null) {
          setIsRowLayoutState(stored === 'true');
        }
      } catch {}
    })();
  }, []);

  // Sync local context to Redux as single source of truth
  useEffect(() => {
    if (reduxLayout) {
      const nextIsRow = reduxLayout === 'row';
      setIsRowLayoutState(prev => {
        if (prev !== nextIsRow) {
          AsyncStorage.setItem(
            IS_ROW_LAYOUT_KEY,
            nextIsRow ? 'true' : 'false',
          ).catch(() => {});
        }
        return nextIsRow;
      });
    }
  }, [reduxLayout]);

  const setIsRowlayout = (value: React.SetStateAction<boolean>) => {
    if (typeof value === 'function') {
      setIsRowLayoutState(prev => {
        const next = (value as (prevState: boolean) => boolean)(prev);
        AsyncStorage.setItem(IS_ROW_LAYOUT_KEY, next ? 'true' : 'false').catch(
          () => {},
        );
        // reflect in Redux (single source of truth)
        dispatch(
          addSingleSetting({
            productCard: { layout: next ? 'row' : 'grid' },
          }),
        );
        return next;
      });
    } else {
      setIsRowLayoutState(value);
      AsyncStorage.setItem(IS_ROW_LAYOUT_KEY, value ? 'true' : 'false').catch(
        () => {},
      );
      // reflect in Redux (single source of truth)
      dispatch(
        addSingleSetting({ productCard: { layout: value ? 'row' : 'grid' } }),
      );
    }
  };

  return (
    <ExtrasContext.Provider
      value={{
        refreshing,
        setRefreshing,
        networkAvailableGlobal,
        setNetworkAvailableGlobal,
        continueOffline,
        setContinueOffline,
        isRowLayout,
        setIsRowlayout,
        receipt,
        setReceipt,
        open,
        setOpen,
      }}>
      {children}
    </ExtrasContext.Provider>
  );
};
